/**
 * Edit Block Serializer
 * Feature: 069-markdown-edits-migration
 *
 * Utility for serializing edit data (crop, light, combined) to markdown blocks.
 * Used by visual editors to generate markdown-encoded edits.
 */

import type { CropData, LightEditData, CombinedEditData } from '@/types/edit.types';
import type { MarkupShape } from '@/types/markup.types';
import logger from '@/utils/logger';

/**
 * Serialize crop and/or light edit data to a markdown ```edit block
 *
 * @param data - Combined edit data (crop and/or light)
 * @returns Markdown block string
 *
 * @example
 * const block = serializeEditToMarkdown({
 *   crop: { x: 10, y: 20, width: 80, height: 60, aspectRatio: 'free' },
 *   light: { exposure: 0.5, contrast: 0, highlights: 0, shadows: 0, whites: 0, blacks: 0 }
 * });
 * // Returns:
 * // ```edit
 * // {"crop":{"x":10,...},"light":{"exposure":0.5,...}}
 * // ```
 */
export function serializeEditToMarkdown(data: Partial<CombinedEditData>): string {
  logger.debug('EditBlockSerializer', 'Serializing edit data', {
    hasCrop: !!data.crop,
    hasLight: !!data.light
  });

  if (!data.crop && !data.light) {
    logger.warn('EditBlockSerializer', 'Cannot serialize empty edit data');
    return '';
  }

  const payload: Partial<CombinedEditData> = {};

  if (data.crop) {
    payload.crop = {
      x: data.crop.x,
      y: data.crop.y,
      width: data.crop.width,
      height: data.crop.height,
      aspectRatio: data.crop.aspectRatio,
    };
    // Only include computedAspectRatio if present
    if (data.crop.computedAspectRatio !== undefined) {
      payload.crop.computedAspectRatio = data.crop.computedAspectRatio;
    }
  }

  if (data.light) {
    payload.light = {
      exposure: data.light.exposure,
      contrast: data.light.contrast,
      highlights: data.light.highlights,
      shadows: data.light.shadows,
      whites: data.light.whites,
      blacks: data.light.blacks,
    };
  }

  const json = JSON.stringify(payload);
  logger.info('EditBlockSerializer', 'Serialized edit block', { jsonLength: json.length });

  return '```edit\n' + json + '\n```';
}

/**
 * Serialize crop data only to a markdown ```edit block
 *
 * @param crop - Crop data
 * @returns Markdown block string
 */
export function serializeCropToMarkdown(crop: CropData): string {
  return serializeEditToMarkdown({ crop });
}

/**
 * Serialize light data only to a markdown ```edit block
 *
 * @param light - Light adjustment data
 * @returns Markdown block string
 */
export function serializeLightToMarkdown(light: LightEditData): string {
  return serializeEditToMarkdown({ light });
}

/**
 * Serialize markup shapes to a markdown ```markup block
 *
 * @param shapes - Array of markup shapes
 * @returns Markdown block string
 *
 * @example
 * const block = serializeMarkupToMarkdown([
 *   { type: 'circle', number: 1, ... }
 * ]);
 * // Returns:
 * // ```markup
 * // {"shapes":[...]}
 * // ```
 */
export function serializeMarkupToMarkdown(shapes: MarkupShape[]): string {
  logger.debug('EditBlockSerializer', 'Serializing markup shapes', { count: shapes.length });

  if (shapes.length === 0) {
    logger.warn('EditBlockSerializer', 'Cannot serialize empty shapes array');
    return '';
  }

  // Remove id field as it gets regenerated on parse
  const cleanedShapes = shapes.map(shape => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...rest } = shape;
    return rest;
  });

  const payload = { shapes: cleanedShapes };
  const json = JSON.stringify(payload);

  logger.info('EditBlockSerializer', 'Serialized markup block', {
    shapeCount: shapes.length,
    jsonLength: json.length
  });

  return '```markup\n' + json + '\n```';
}
